// Applying a site language, in one place.
//
// Three things now switch the language: the navbar selector, the /kn|/gu|/bn|/ar
// route modes, and the language dialog on the calculator. They were drifting —
// the navbar set sessionStorage and drove the widget but never wrote the cookie,
// so a choice made there only survived a hard reload because useLanguageMode
// happened to re-write it on the next mount. Three half-implementations of the
// same operation is how a language silently fails to stick on one path only.
//
// So the operation lives here and every caller uses it.

const RTL_LANGS = new Set(["ar", "he", "iw", "fa", "ur"]);

/** Google Translate never flips layout direction, so we do it. */
export function applyDirection(lang: string | null) {
  const rtl = !!lang && RTL_LANGS.has(lang);
  document.documentElement.setAttribute("dir", rtl ? "rtl" : "ltr");
  document.body.classList.toggle("aura-rtl", rtl);
}

/**
 * Drive the hidden Google Translate <select>.
 *
 * The Google script is async, so the select may not exist yet on a fresh load.
 * Poll for it rather than failing silently, and give up after ~10s so a blocked
 * script does not leave an interval running forever.
 */
function driveWidget(lang: string): () => void {
  let tries = 0;
  const interval = setInterval(() => {
    const select = document.querySelector(
      "#google_translate_element select",
    ) as HTMLSelectElement | null;
    if (select) {
      clearInterval(interval);
      select.value = lang;
      select.dispatchEvent(new Event("change", { bubbles: true }));
    } else if (++tries > 40) {
      clearInterval(interval);
    }
  }, 250);
  return () => clearInterval(interval);
}

/**
 * Switch the whole site to `lang`. Returns a cleanup that cancels the widget
 * poll if the caller unmounts first.
 *
 * The COOKIE is the part that matters beyond this page load: it is what a hard
 * reload reads. The compatibility calculator sends people through Google
 * sign-in, which is a full page navigation away and back, and without the
 * cookie they would return to an English page having already chosen Hindi.
 */
export function applySiteLanguage(lang: string): () => void {
  try {
    sessionStorage.setItem("auraLang", lang);
  } catch {
    /* storage blocked: the choice just will not outlive this page */
  }

  if (!lang || lang === "en") {
    try {
      // Expire on both host and dotted-domain forms; Translate writes either.
      const dead = "googtrans=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT";
      document.cookie = dead;
      document.cookie = `${dead};domain=.${window.location.hostname}`;
    } catch {
      /* ignore */
    }
    document.body.classList.remove("aura-lang-mode");
    applyDirection(null);
    return driveWidget("en");
  }

  try {
    const cookie = `googtrans=/en/${lang};path=/`;
    document.cookie = cookie;
    document.cookie = `${cookie};domain=.${window.location.hostname}`;
  } catch {
    /* ignore */
  }
  document.body.classList.add("aura-lang-mode");
  applyDirection(lang);
  return driveWidget(lang);
}

/** The active site language, or null when the visitor is still on English. */
export function activeSiteLanguage(): string | null {
  try {
    // Cookie and sessionStorage can disagree for a moment after a switch, so
    // treat EITHER as "already chosen" rather than re-prompting in the gap.
    const cookie = document.cookie.match(/googtrans=\/[^/]*\/([^;]+)/);
    const fromCookie = cookie?.[1];
    if (fromCookie && fromCookie !== "en") return fromCookie;
    const stored = sessionStorage.getItem("auraLang");
    if (stored && stored !== "en") return stored;
  } catch {
    /* cookies or storage blocked */
  }
  return null;
}
